import { ReactElement, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import colors from '../colors';
import { baseTyping } from '../typings';

type ButtonI = {
  noFill?: boolean;
  roundRight?: boolean;
  roundLeft?: boolean;
  label?: ReactNode;
  icon?: ReactElement;
  as?: 'button' | 'a';
};

export const BaseButtonStyles = css<ButtonI>`
  height: 40px;
  padding: ${(props) =>
    props.label && props.icon ? '8px 24px 8px 16px' : '8px 16px'};
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  ${baseTyping}
  font-weight: 400;
  font-size: 16px;
  line-height: 23px;
  text-align: center;

  & svg {
    margin: ${(props) => (props.label ? '0 5px 0 0' : '0')};
    width: 16px;
    height: 16px;
  }
`;

const borderRadiusLeft = css`
  border-radius: unset;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
`;

const borderRadiusRight = css`
  border-radius: unset;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;
export const BaseButton = styled.button`
  ${BaseButtonStyles}
`;

export const PrimaryButton = styled(BaseButton)<ButtonI>`
  color: ${colors.white};
  ${({ roundRight }) => roundRight && borderRadiusRight};
  ${({ roundLeft }) => roundLeft && borderRadiusLeft};
  background-color: ${colors.green};

  & svg {
    & path {
      fill: ${(props) => (props.noFill ? 'none' : colors.white)};
    }
  }

  &:hover {
    background-color: ${colors.greenDark};
  }

  &:active {
    background-color: ${colors.greenDark};
    color: ${colors.lighterGrey};

    & svg {
      & path {
        fill: ${colors.lightGrey};
      }
    }
  }

  &:disabled {
    background-color: ${colors.grey};
    cursor: unset;
  }
`;

export const SecondaryButton = styled(BaseButton)`
  border: 1.5px solid ${colors.green};
  color: ${colors.green};
  background-color: inherit;

  & svg {
    & path {
      stroke: ${colors.green};
    }
  }

  &:hover {
    background-color: ${colors.green};
    color: ${colors.white}!important;

    & svg {
      & path {
        fill: ${colors.white};
        stroke: ${colors.white};
      }
    }
  }

  &:active {
    background-color: ${colors.greenDark};
    color: ${colors.lightGrey};

    & svg {
      & path {
        fill: ${colors.lightGrey};
      }
    }
  }

  &:disabled {
    border: 1.5px solid ${colors.grey};
    color: ${colors.grey};
    background-color: inherit;
    cursor: unset;

    & svg {
      & path {
        fill: ${colors.grey};
        stroke: ${colors.grey};
      }
    }
  }
`;

export const TertiaryButton = styled.button`
  ${BaseButtonStyles}
  background-color: ${colors.lighterGrey};
  color: ${colors.green};

  &:hover {
    background-color: ${colors.lightGrey};
    color: ${colors.greenDark};

    & svg {
      & path {
        fill: ${colors.greenDark};
      }
    }
  }

  &:active {
    background-color: ${colors.grey};
    color: ${colors.greenDark};

    & svg {
      & path {
        fill: ${colors.greenDark};
      }
    }
  }

  &:disabled {
    background-color: ${colors.lighterGrey};
    color: ${colors.grey};
    cursor: unset;

    & svg {
      & path {
        fill: ${colors.grey};
      }
    }
  }
`;

export const DangerButton = styled(TertiaryButton)`
  color: ${colors.red};

  &:hover {
    color: ${colors.red};

    & svg {
      & path {
        fill: ${colors.red};
      }
    }
  }

  &:active {
    color: ${colors.red};

    & svg {
      & path {
        fill: ${colors.red};
      }
    }
  }
`;

export const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: inherit;

  & svg {
    & path {
      fill: ${colors.green};
    }
  }

  &:hover {
    background-color: ${colors.lighterGrey};
  }

  &:active {
    background-color: ${colors.lightGrey};

    & svg {
      & path {
        fill: ${colors.greenDark};
      }
    }
  }

  &:disabled {
    opacity: 0;
  }
`;

export const CloseButton = styled.button`
  padding: 12px;
  position: fixed;
  top: 20px;
  right: 40px;
  background-color: white;
  border: none;
  border-radius: 8px;
  line-height: unset;
  font-size: 0;
  cursor: pointer;
  z-index: 9999;
`;

const styledButtons = {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  DangerButton,
  IconButton,
  CloseButton,
};

export default styledButtons;
